import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { IPrintableReportHeading } from '@models/report-print-heading';
import {
  BenchmarkStandardsOverTimeReport
} from '@shared/branded-reports/branded-benchmark-reports/admin-standards-over-time-report/standards-over-time-report-detail.model';
import {
  BarColors,
  BorderColors,
  BorderStyles,
  TextColors
} from '@shared/branded-reports/zbchart-report/bar-colors.enum';
import { ZbChartReportTableComponent } from '@shared/branded-reports/zbchart-report/zbchart-report-table.component';
import { ZbChartBar, ZbChartBarValue, ZbChartData } from '@shared/branded-reports/zbchart-report/zbchart-report.model';
import { BehaviorSubject, Subscription } from 'rxjs';

@Component({
  selector: 'zbp-standards-over-time-report-detail',
  imports: [
    ZbChartReportTableComponent
  ],
  templateUrl: './standards-over-time-report-detail.component.html',
})
export class StandardsOverTimeReportDetailComponent implements OnInit, OnChanges, OnDestroy {
  @Input() standardsOverTimeReport?: BenchmarkStandardsOverTimeReport;
  @Input() isPrinting: boolean;
  @Input() printableReportHeadings: IPrintableReportHeading[];

  zbChart: ZbChartData;
  private reports$ = new BehaviorSubject<BenchmarkStandardsOverTimeReport>(null);
  private readonly subscriptions: Subscription = new Subscription();

  ngOnInit(): void {
    this.subscriptions.add(
      this.reports$.subscribe((report) => {
        this.createZbChart(report);
      })
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.standardsOverTimeReport) {
      this.reports$.next(changes.standardsOverTimeReport.currentValue);
    }

    /** When this input changes, the print function is executed */
    if (changes.isPrinting) {
      if (this.isPrinting) {
        this.print();
      }
    }
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  createZbChart(
    report: BenchmarkStandardsOverTimeReport,
  ): ZbChartData {
    if (!report) {
      return null;
    }

    const chartData = { bars: [] } as ZbChartData;
    chartData.showZeroCount = true;

    report.benchmarkResults.forEach((result) => {
      let ariaLabel: string;
      const row = {} as ZbChartBar;
      const values = [] as ZbChartBarValue[];

      row.label = result.assessmentName;
      row.labelOnLeft = false;

      if (Number.isFinite(result.assessmentPercentageChange)) {
        row.totalCount = 100;
        ariaLabel = `Average score for this assessment is ${result.displayValue}`;
        values.push({
          barLabel: result.displayValue,
          barTextColor: TextColors.black,
          color: this.determineBarColor(result.assessmentPercentageChange),
          borderColor: this.determineBorderColor(result.assessmentPercentageChange),
          percent: result.assessmentPercentageChange,
          count: result.assessmentPercentageChange,
        });
      } else {
        const upperDisplayValue = (result.displayValue ?? '').toUpperCase();
        row.fullLengthButton = true;
        ariaLabel = upperDisplayValue;
        values.push({
          barLabel: upperDisplayValue,
          color: BarColors.white,
          borderColor: BorderColors.borderForWhite,
          legendId: 'notAssigned',
          borderStyle: BorderStyles.dashed
        });
      }

      row.values = values;
      row.ariaLabel = ariaLabel;

      if (result.displayTooltip) {
        row.tooltip = result.displayTooltip;
      }
      chartData.bars.push(row);
    });

    this.zbChart = chartData;

    return chartData;
  }

  determineBarColor(percent: number): BarColors {
    let color: BarColors;
    if (!percent) {
      color = null;
    } else if (percent < 70) {
      color = BarColors.red300;
    } else if (percent < 90) {
      color = BarColors.cream300;
    } else {
      color = BarColors.green300;
    }
    return color;
  }

  determineBorderColor(percent: number): BorderColors {
    let color: BorderColors;
    if (!percent) {
      color = null;
    } else if (percent < 70) {
      color =  BorderColors.borderForRed300;
    } else if (percent < 90) {
      color =  BorderColors.borderForCream300;
    } else {
      color = BorderColors.borderForGreen300;
    }
    return color;
  }

  /**
   * Prints report.
   */
  print() {
    // Give time for Printable Header to render
    setTimeout(() => {
      window.print();
    }, 0);
  }
}
